import { useContext, useState } from "react";
import { AuthContext, AuthContextType } from "../../../auth/Auth";
import { useLoginState } from "@auth/LoginState";
import { useFormik } from "formik";
import {
  Typography,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ProfileInterface } from "ts-interfaces";
import { produce } from "immer";
import { identifyUser, trackEvent } from "@libs/analyticsManager";
import { ProfileUpload } from "@/components/ProfileUpload";
import { PrimaryButton } from "@/studioxr/Button";

const specializationOptions = [
  "Residential Architecture",
  "Residential Architecture & Interior Design",
  "Commercial Architecture",
  "Commercial Interior Design",
  "Commercial & Residential Architecture",
  "Commercial & Residential Interior Design",
  "Commercial & Residential Architecture & Interior Design",
  "Hospitality/Retail Architecture",
  "Hospitality/Retail Interior Design",
  "Institution/Education",
  "Transportation",
  "Technology",
  "Workplace",
  "Stage/Set/Theatre",
  "Multi Discipline/Specialization",
  "Other",
];

export default function ProfileForm({
  initialProfile,
  onSuccess,
  createProfile = false,
}) {
  const { currentUser } = useContext(AuthContext) as AuthContextType;
  const [pic, setPic] = useState({});
  const [showFlash, setShowFlash] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitButtonDetails, setSubmitButtonDetails] = useState({
    label: "Save changes",
    disable: false,
  });
  const [flashMessage, setFlashMessage] = useState("");
  const profile: any = useLoginState((state) => state.profile);
  const updateAndSaveProfile = useLoginState(
    (state) => state.updateAndSaveProfile,
  );

  const formik = useFormik({
    //validationSchema: formSchema,
    initialValues: {
      firstName: initialProfile?.first_name || profile?.first_name || "",
      lastName: initialProfile?.last_name || profile?.last_name || "",
      companyName: initialProfile?.company_name || "",
      companySize: initialProfile?.company_size || "",
      industry: initialProfile?.industry || "",
      measuringCategory:
        initialProfile?.measuring_category ||
        initialProfile?.measuringCategory ||
        profile?.measuringCategory ||
        "Imperial",
      specialization:
        initialProfile?.specialization &&
        specializationOptions.includes(initialProfile?.specialization)
          ? initialProfile?.specialization
          : "",
      occupation: initialProfile?.occupation || "",
      email: initialProfile?.email || profile?.email || "",
    },
    onSubmit: (values) => {},
    enableReinitialize: true,
  });

  const { handleChange, setFieldValue, values } = formik;

  const handleProfilePicChange = (blob) => {
    if (blob) setPic({ pic: blob });
  };

  const handleSubmit = () => {
    if (!values.companySize || values.companySize.trim() === "") {
      setFlashMessage("Please enter company size");
      setShowFlash(true);
      return;
    }
    if (!values.occupation) {
      setFlashMessage("Please enter occupation");
      setShowFlash(true);
      return;
    }
    if (!values.specialization) {
      setFlashMessage("Please enter specialization");
      setShowFlash(true);
      return;
    }    

    if (!profile?.id || !profile?.uid) {
      throw new Error("Profile not found");
    }
    if (profile.uid !== currentUser.uid) {
      throw new Error("Profile id does not match current user id");
    }
    setFlashMessage("");
    setShowFlash(false);
    setSubmitButtonDetails({ label: "Saving...Please Wait", disable: true });

    const updatedProfile: ProfileInterface = produce(
      profile as ProfileInterface,
      (draft) => {
        draft.first_name = values.firstName;
        draft.last_name = values.lastName;
        draft.company_name = values.companyName;
        draft.company_size = values.companySize;
        draft.industry = values.industry;
        draft.specialization = values.specialization;
        draft.occupation = values.occupation;
        draft.measuring_category = values.measuringCategory;
      },
    );

    identifyUser({ currentUser, profile: updatedProfile });
    if (createProfile) {
      trackEvent("signup_complete_profile");
    }

    updateAndSaveProfile(updatedProfile, pic)
      .then((resp) => {
        setShowSuccess(true);
        onSuccess?.(resp);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      })
      .catch((error) => {
        setSubmitButtonDetails({
          label: initialProfile ? "Save changes" : "Next",
          disable: false,
        });
      })
      .finally(() => {
        setSubmitButtonDetails({
          label: initialProfile ? "Save changes" : "Next",
          disable: false,
        });
      });
  };

  if (!profile?.id) return null;
  return (
    <>
      <Typography component="h1" variant="h5" className="login-title" sx={{ marginBottom: "4px!important" }}>
        {createProfile ? "Create your profile" : "Edit your profile"}
      </Typography>
      <div className="login-form">
        {showFlash && (
          <Typography component="h6" color="error" align="center" variant="h5">
            {flashMessage}
          </Typography>
        )}

        <Stack alignItems="stretch">
          <Stack direction="row">
            <TextField
              variant="outlined"
              sx={{ paddingRight: "10px", marginTop: "10px", marginBottom: "10px" }}
              size="small"
              id="firstName"
              type="text"
              label="First name"
              name="firstName"
              value={values.firstName}
              onChange={handleChange}
            />
            <TextField
              variant="outlined"
              sx={{ marginTop: "10px", marginBottom: "10px" }}
              size="small"
              id="lastName"
              type="text"
              label="Last name"
              name="lastName"
              value={values.lastName}
              onChange={handleChange}
            />
          </Stack>
          <TextField
            variant="outlined"
            sx={{ marginTop: "10px", marginBottom: "10px" }}
            fullWidth
            size="small"
            id="email"
            type="text"
            label="Email"
            name="email"
            value={profile?.email}
            disabled
          />
          <TextField
            variant="outlined"
            sx={{ marginTop: "10px", marginBottom: "10px" }}
            fullWidth
            size="small"
            id="companyName"
            type="text"
            label="Company name"
            name="companyName"
            value={values.companyName}
            onChange={(e) => setFieldValue("companyName", e.target.value)}
          />

          <Stack direction="row">
            <FormControl
              required
              fullWidth
              sx={{ paddingRight: "10px" }}
              className="custom-select"
            >
              <InputLabel id="company-size-label" sx={{ marginTop: "-5px" }}>
                Size of company
              </InputLabel>
              <Select
                size="small"
                labelId="company-size-label"
                id="company-size"
                label="Size of company"
                value={values.companySize}
                onChange={(e) => setFieldValue("companySize", e.target.value)}
              >
                <MenuItem value={"1-5"}>1-5</MenuItem>
                <MenuItem value={"6-20"}>6-20</MenuItem>
                <MenuItem value={"20+"}>20+</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth required className="custom-select">
              <InputLabel id="occupation-label" sx={{ marginTop: "-5px" }}>
                Occupation
              </InputLabel>
              <Select
                size="small"
                labelId="occupation-label"
                id="occupation"
                label="Occupation"
                value={values.occupation}
                onChange={(e) => setFieldValue("occupation", e.target.value)}
              >
                <MenuItem value={"Business Owner"}>Business Owner</MenuItem>
                <MenuItem value={"Executive Employee"}>
                  Executive Employee
                </MenuItem>
                <MenuItem value={"Design/Admin Employee"}>
                  Design/Admin Employee
                </MenuItem>
                <MenuItem value={"Sales Employee"}>Sales Employee</MenuItem>
                <MenuItem value={"Student"}>Student</MenuItem>
                <MenuItem value={"Hobbyist"}>Hobbyist</MenuItem>
                <MenuItem value={"Other"}>Other</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction="row">
            <FormControl fullWidth required className="custom-select" sx={{ paddingRight: "10px" }}>
              <InputLabel
                id="company-specialization-label"
                sx={{ marginTop: "-5px" }}
              >
                Specialization
              </InputLabel>
              <Select
                size="small"
                labelId="company-specialization-label"
                id="company-specialization"
                label="Specialization"
                value={values.specialization}
                onChange={(e) =>
                  setFieldValue("specialization", e.target.value)
                }
              >
                {specializationOptions.map((value) => (
                  <MenuItem key={value} value={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl
              fullWidth
              className="custom-select"
            >
              <InputLabel id="industry-label" sx={{ marginTop: "-5px" }}>
                Industry
              </InputLabel>
              <Select
                size="small"
                labelId="industry-label"
                id="industry"
                label="Industry"
                value={values.industry}
                onChange={(e) => setFieldValue("industry", e.target.value)}
              >
                <MenuItem value={"Architecture"}>Architecture</MenuItem>
                <MenuItem value={"Interior Design"}>Interior Design</MenuItem>
                <MenuItem value={"Graphics"}>Graphics</MenuItem>
                <MenuItem value={"Fashion"}>Fashion</MenuItem>
                <MenuItem value={"Real Estate"}>Real Estate</MenuItem>
                <MenuItem value={"Construction"}>Construction</MenuItem>
                <MenuItem value={"Manufacturer/Distributor"}>
                  Manufacturer/Distributor
                </MenuItem>
                <MenuItem value={"Set/Stage/Theatre Design"}>
                  Set/Stage/Theatre Design
                </MenuItem>
                <MenuItem value={"Product Design"}>Product Design</MenuItem>
                <MenuItem value={"Design Hobbyist"}>Design Hobbyist</MenuItem>
                <MenuItem value={"Other"}>Other</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <FormControl fullWidth required className="custom-select">
            <InputLabel id="measuringCategory-label">Units</InputLabel>
            <Select
              size="small"
              labelId="measuringCategory-label"
              id="measuringCategory"
              label="measuringCategory"
              value={values.measuringCategory}
              onChange={(e) =>
                setFieldValue("measuringCategory", e.target.value)
              }
            >
              <MenuItem value={"Metric"}>Metric (meters)</MenuItem>
              <MenuItem value={"Imperial"}>Imperial (inches)</MenuItem>
            </Select>
          </FormControl>

          <div style={{ marginTop: "8px" }}>
            <ProfileUpload
              onFileChange={handleProfilePicChange}
              img={initialProfile?.files?.pic}
              onDelete={() => {}}
            />
          </div>
          {showSuccess && (
            <Typography
              component="h6"
              color="success"
              align="center"
              variant="h5"
              style={{ marginTop: "16px" }}
            >
              Profile successfully {createProfile ? "created" : "updated"}!
            </Typography>
          )}
          <PrimaryButton
            style={{ marginTop: "20px", marginBottom: "16px" }}
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={submitButtonDetails.disable}
            aria-label="submit-btn"
          >
            {submitButtonDetails.label}
          </PrimaryButton>
        </Stack>
      </div>
    </>
  );
}
